import { toast } from 'react-hot-toast';
import { setBlurLoading, setToken } from '../../redux/slices/authSlice';
import { apiConnector } from '../apiconnector';
import { authEndpoints } from '../apis';
import { setUser } from '../../redux/slices/profileSlice';
import { resetWishlist } from '../../redux/slices/wishlistSlice';

const {
    RESETPASSTOKEN_API,
    RESETPASSWORD_API,
    SENDOTP_API,
    SIGNUP_API,
    LOGIN_API,
    UPDATE_PASSWORD_API,
} = authEndpoints;

export const getPasswordResetToken = (email, setEmailSent, setConfirmationModal) => {
    return async (dispatch) => {
        dispatch(setBlurLoading(true));
        try {
            const response = await apiConnector('POST', RESETPASSTOKEN_API, { email });
            if (!response.data.success) {
                setConfirmationModal({
                    text1: "Oops ❗️",
                    text2: response.data.message,
                    closeModal: () => setConfirmationModal(null),
                })
                return;
            }
            setEmailSent(true);
        } catch (error) {
            console.log('Reset Password token generation error ', error.message);
            setConfirmationModal({
                text1: "Oops ❗️",
                text2: "We apologize for the inconvenience. The password reset link creation has encountered an internal server error. Please try again. If the issue persists, please don't hesitate to contact us for assistance.",
                closeModal: () => setConfirmationModal(null),
            })
        } finally {
            dispatch(setBlurLoading(false));
        }
    };
};

export const resetPassword = (password, confirmPassword, token, navigate, setConfirmationModal) => {
    return async (dispatch) => {
        dispatch(setBlurLoading(true));
        try {
            const response = await apiConnector('POST', RESETPASSWORD_API, { password, confirmPassword, token });
            if (!response.data.success) {
                if (!response.data.success) {
                    setConfirmationModal({
                        text1: "Link Expired ❗️",
                        text2: response.data.message,
                        btn1Text: 'Resend Link',
                        btn1Handler: () => navigate('/forgot-password'),
                        closeModal: () => setConfirmationModal(null),
                    })
                    return;
                }
            } else {
                setConfirmationModal({
                    text1: "Success ✅",
                    text2: "Your password has been reset successfully. Welcome back to your account. You can now log in and regain access.",
                    btn1Text: "Login",
                    btn1Handler: () => navigate('/login'),
                    closeModal: () => setConfirmationModal(null),
                })
            }
        } catch (error) {
            console.log('Password update failed ', error.message);
            setConfirmationModal({
                text1: "Oops ❗️",
                text2: "We apologize for the inconvenience. The password reset process has encountered an internal server error. Please try again. If the issue persists, please don't hesitate to contact us for assistance.",
                closeModal: () => setConfirmationModal(null),
            })
        } finally {
            dispatch(setBlurLoading(false));
        }
    };
};

export const sendOtp = (firstName, lastName, email, navigate, setConfirmationModal) => {
    return async (dispatch) => {
        dispatch(setBlurLoading(true));
        try {
            const response = await apiConnector('POST', SENDOTP_API, {
                firstName,
                lastName,
                email,
                checkUserExist: true
            });
            if (!response.data.success) {
                setConfirmationModal({
                    text1: "Signup Failed ❗️",
                    text2: response.data.message,
                    closeModal: () => setConfirmationModal(null),
                })
                return;
            }
            navigate('/verify-email');
        } catch (error) {
            console.log('OTP send failed ', error.message);
        } finally {
            dispatch(setBlurLoading(false));
        }

    };
};

export const signUp = (accountType, firstName, lastName, email, password, confirmPassword, otp, navigate, setConfirmationModal) => {
    return async (dispatch) => {
        dispatch(setBlurLoading(true));
        try {
            const response = await apiConnector("POST", SIGNUP_API, { firstName, lastName, email, password, confirmPassword, accountType, otp });
            if (!response.data.success) {
                setConfirmationModal({
                    text1: "Validation Failed ❗️",
                    text2: response.data.message,
                    closeModal: () => setConfirmationModal(null),
                })
                return;
            }
            setConfirmationModal({
                text1: "Success ✅",
                text2: "Welcome to our community! Your account has been successfully created. Please proceed to log in to access your account.",
                btn1Text: "Login",
                btn1Handler: () => navigate('/login'),
                closeModal: () => setConfirmationModal(null),
            })
        } catch (error) {
            console.log('Signup failed due to ', error.message);
            toast.error('Signup Failed! Try Again Otherwise Contact Us')
        } finally {
            dispatch(setBlurLoading(false));
        }
    };
};

export const login = (email, password, navigate, setConfirmationModal) => {
    return async (dispatch) => {
        dispatch(setBlurLoading(true));
        try {
            const response = await apiConnector("POST", LOGIN_API, { email, password }, { withcredentials: true });
            if (!response.data.success) {
                setConfirmationModal({
                    text1: 'Login Failed ❗️',
                    text2: response.data.message,
                    closeModal: () => setConfirmationModal(null),
                })
                return;
            }
            dispatch(setToken(response.data.token));
            const userImage = response.data?.user?.image ?
                response.data.user.image :
                `https://api.dicebear.com/5.x/initials/svg?seed=${response.data.user.firstName} ${response.data.user.lastName}`;
            dispatch(setUser({ ...response.data.user, image: userImage }));
            localStorage.setItem('token', JSON.stringify(response.data.token));
            localStorage.setItem('user', JSON.stringify(response.data.user));

            navigate('/dashboard/profile');
        } catch (error) {
            console.log('Login failed due to ', error.message);
        } finally {
            dispatch(setBlurLoading(false));
        }

    };
};

export const logOut = (navigate) => {
    return (dispatch) => {
        dispatch(setBlurLoading(true));
        try {
            dispatch(setToken(null));
            dispatch(setUser(null));
            dispatch(resetWishlist());
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            navigate('/');
        } catch (error) {
            console.log('Logout Failed => ', error);
        } finally {
            dispatch(setBlurLoading(false));
        }
    };
};

export const updatePassword = (oldPassword, newPassword, email, firstName) => {
    return async (dispatch) => {
        dispatch(setBlurLoading(true));
        try {
            const response = await apiConnector("POST", UPDATE_PASSWORD_API, {
                oldPassword,
                newPassword,
                email,
                firstName
            });
            if (!response.data.success) {
                return toast.error(response.data.message);
            }
            toast.success(response.data.message, { duration: 3000 });
        } catch (error) {
            console.log('error in update password ', error.message);
            toast.error('Password updation failed try again or contact us');
        } finally {
            dispatch(setBlurLoading(false));
        }
    };
};